import React from 'react';

// Constantes de servicios
const SERVICES = [
  {
    icon: '💻',
    title: 'Desarrollo Web',
    description: 'Sitios web modernos y responsivos con las últimas tecnologías.',
    color: 'from-blue-500 to-cyan-500'
  },
  {
    icon: '🎨',
    title: 'Diseño UI/UX',
    description: 'Interfaces intuitivas y atractivas que mejoran la experiencia del usuario.',
    color: 'from-purple-500 to-pink-500'
  },
  {
    icon: '📱',
    title: 'Aplicaciones Web',
    description: 'Aplicaciones web progresivas y aplicaciones móviles híbridas.',
    color: 'from-green-500 to-emerald-500'
  },
  {
    icon: '⚡',
    title: 'Optimización SEO',
    description: 'Mejora el posicionamiento de tu sitio web en los motores de búsqueda.',
    color: 'from-orange-500 to-red-500'
  },
  {
    icon: '🛠️',
    title: 'Mantenimiento',
    description: 'Soporte técnico continuo y actualizaciones regulares de tu sitio web.',
    color: 'from-indigo-500 to-purple-500'
  },
  {
    icon: '🚀',
    title: 'Consultoría Digital',
    description: 'Asesoramiento estratégico para transformar tu presencia digital.',
    color: 'from-teal-500 to-blue-500'
  }
];

// Delays de animación
const ANIMATION_DELAYS = {
  services: {
    cards: ['0.1s', '0.2s', '0.3s', '0.4s', '0.5s', '0.6s']
  }
};

const Services: React.FC = () => {
  return (
    <section id="servicios" className="relative w-full min-h-screen overflow-hidden bg-gradient-to-b from-[#000] to-black text-white flex flex-col items-center justify-center px-4 -mt-px pt-20 ">
      {/* Elementos de fondo específicos de servicios - Más notorios */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 left-10 w-80 h-80 bg-purple-500/15 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-1/2 right-10 w-80 h-80 bg-cyan-500/15 rounded-full blur-3xl animate-pulse-slow" style={{animationDelay: '1.5s'}}></div>
      </div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 animate-slide-up">
            Nuestros <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-blue-600 bg-clip-text text-transparent animate-gradient">Servicios</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto animate-slide-down">
            Ofrecemos soluciones web completas para hacer crecer tu negocio digital
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {SERVICES.map((service, index) => (
            <div
              key={index}
              className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-8 hover:shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 animate-scale-in hover:scale-105 group"
              style={{animationDelay: ANIMATION_DELAYS.services.cards[index]}}
            >
              <div className={`w-16 h-16 bg-gradient-to-r ${service.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <span className="text-2xl">{service.icon}</span>
              </div>
              
              <h3 className="text-2xl font-bold text-white mb-4">{service.title}</h3>
              <p className="text-gray-300 leading-relaxed">{service.description}</p>
            </div>
          ))}
        </div>
        
        {/* Sección ¿Por qué elegirnos? */}
        <div className="mt-16 mb-16 animate-fade-in">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 animate-slide-up">
              ¿Por qué <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-blue-600 bg-clip-text text-transparent animate-gradient">elegirnos</span>?
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto animate-slide-down">
              Descubre lo que nos hace diferentes y por qué somos la mejor opción para tu proyecto
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Innovación Tecnológica */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-center hover:shadow-xl hover:shadow-blue-500/25 transition-all duration-300 animate-scale-in hover:scale-105">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Innovación Tecnológica</h3>
              <p className="text-gray-300 text-sm">Utilizamos las últimas tecnologías y tendencias del mercado</p>
            </div>
            
            {/* Calidad */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-center hover:shadow-xl hover:shadow-blue-500/25 transition-all duration-300 animate-scale-in hover:scale-105">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Calidad Premium</h3>
              <p className="text-gray-300 text-sm">Código limpio, diseño moderno y funcionalidad excepcional</p>
            </div>
            
            {/* Soporte */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-center hover:shadow-xl hover:shadow-blue-500/25 transition-all duration-300 animate-scale-in hover:scale-105">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Soporte 24/7</h3>
              <p className="text-gray-300 text-sm">Estamos aquí para ayudarte en cualquier momento</p>
            </div>
            
            {/* Resultados */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-center hover:shadow-xl hover:shadow-blue-500/25 transition-all duration-300 animate-scale-in hover:scale-105">
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Resultados Garantizados</h3>
              <p className="text-gray-300 text-sm">Nos comprometemos a entregar resultados que superen tus expectativas</p>
            </div>
          </div>
        </div>
        
        {/* CTA Section */}
        <div className="text-center mb-24 animate-fade-in">
          <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-md border border-blue-500/30 rounded-3xl p-8 max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-white mb-4">
              ¿Listo para empezar tu proyecto?
            </h3>
            <p className="text-gray-300 mb-8 max-w-xl mx-auto">
              En OpenWeb Studio, convertimos tus ideas en experiencias digitales excepcionales. 
              Contáctanos y descubre cómo podemos ayudarte a alcanzar tus objetivos.
            </p>
            <button
              onClick={() => {
                const contactSection = document.getElementById('contacto');
                if (contactSection) {
                  contactSection.scrollIntoView({ 
                    behavior: 'smooth',
                    block: 'start'
                  });
                }
              }}
              className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:shadow-blue-500/25 hover:scale-105 transition-all duration-300"
            >
              ¡Empezar Ahora!
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services; 